import { useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TIP_SLIDE_MS } from '@/config/pageTips';
import { type SiteTipSlide } from '@/content/tips';

/**
 * Which `aria-live` politeness the rotating region carries.
 *
 * `off` while the deck is turning by itself and `polite` once the visitor is
 * steering it, which is the WAI-ARIA carousel pattern's answer to a genuine
 * conflict. An auto-rotating region that announces every change interrupts
 * whatever a screen reader user is actually reading, three times a deck, on
 * sixteen pages — the exact nagging this feature is built not to do. But a
 * visitor who has just pressed a dot has asked for the new slide and should
 * hear that it arrived, because the control they pressed does not move focus
 * and nothing else would tell them.
 */
export type TipLive = 'off' | 'polite';

export type TipCarousel = {
  /** Which slide is showing. Also the `key` the card's transition runs on. */
  index: number;
  slide: SiteTipSlide;
  total: number;
  /**
   * Which way the deck last turned: `1` forward, `-1` back.
   *
   * The slide transition needs it, and nothing else does. A carousel that
   * animates every change in the same direction is disorienting the moment a
   * visitor presses "previous" and watches the content leave the way it would
   * have left on "next" — so the direction is tracked here, where the thing
   * that caused the change knows it, rather than inferred in the component
   * from a previous index it would have to remember itself.
   */
  direction: 1 | -1;
  /**
   * Whether the deck is advancing by itself *right now* — false while the
   * visitor has paused it and false while the pointer or the keyboard is
   * resting on the card. It is what the slide transition and the live region
   * are decided from, not what the pause button reads from.
   */
  rotating: boolean;
  /**
   * The visitor's own decision, which is a different thing from `rotating`:
   * this is what the pause button shows, so a deck that has merely gone quiet
   * under the pointer does not flip the button to "play" as the mouse passes
   * over it. That flicker is the whole reason the two are separate.
   */
  paused: boolean;
  togglePaused: () => void;
  goTo: (index: number) => void;
  next: () => void;
  previous: () => void;
  live: TipLive;
  /**
   * Hover and focus suspend the rotation; spread onto the carousel region.
   *
   * Kept here rather than written out in the component because all four
   * handlers are one decision and splitting them across two files is how one
   * of them ends up missing. `focus`/`blur` and not `focusin`/`focusout`:
   * React's synthetic focus events already bubble, so these fire for the dots
   * and the buttons inside the region as well as the region itself.
   */
  suspendProps: {
    onPointerEnter: () => void;
    onPointerLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
};

/**
 * A page's tip deck, turning itself over.
 *
 * ## The rules, and which of them are obligations
 *
 * **Required.** Moving content that runs for more than five seconds has to be
 * pausable (WCAG 2.2.2), and every control that pauses or steers it has to be
 * reachable from the keyboard (2.1.1). So: a real pause button with a name
 * that says what pressing it does, dots that are buttons, and arrow keys on
 * the region — see the card.
 *
 * **Expected, and not strictly required.** Rotation suspends while the pointer
 * is over the card or the keyboard is inside it, because a slide turning under
 * a reader who has stopped to read it is the single most irritating thing a
 * carousel does. And it stops **for good** the moment the visitor steers the
 * deck themselves: somebody who has pressed a dot has taken control of the
 * thing, and quietly resuming the timer behind them would take it back.
 *
 * **Our own.** Under `prefers-reduced-motion` the deck starts paused on slide
 * one. The criterion that covers reduced motion is about animation rather than
 * about content changing, so nothing obliges this — but a visitor who has
 * asked the whole system for less movement is not asking for a card that
 * reshuffles itself three times, and the three slides are all still there
 * behind the dots. `MotionConfig reducedMotion="user"` handles the transition
 * *between* slides; it cannot know that the rotation itself is optional, which
 * is why this is read here as well.
 *
 * ## Why a chained timeout and not an interval
 *
 * The timer is restarted by the effect below whenever the index or the
 * rotation state changes, which gives the one behaviour an interval cannot:
 * a visitor who hovers the card four seconds into a slide, reads it, and
 * moves the pointer away gets a **full** seven seconds on the next slide
 * rather than the three that were left on a shared clock. The slide they
 * paused on is also the slide they are still looking at when they leave, so
 * it gets its own clean interval too.
 */
export function useTipCarousel(slides: readonly SiteTipSlide[]): TipCarousel {
  const total = slides.length;
  const prefersReducedMotion = useReducedMotion();

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  /** Lazy, so the media query decides the initial value rather than an effect
      flipping it after the first frame. The card is only ever mounted long
      after hydration, so there is no server render to disagree with. */
  const [paused, setPaused] = useState(() => prefersReducedMotion === true);
  const [suspended, setSuspended] = useState(false);

  /* A one-slide deck is not a carousel. The type in `content/tips.ts` forbids
     one, but this hook is handed a plain array and should not spin a timer for
     a single slide if that ever changes. */
  const rotating = total > 1 && !paused && !suspended;

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setTimeout(() => {
      setDirection(1);
      setIndex((at) => (at + 1) % total);
    }, TIP_SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [rotating, index, total]);

  /**
   * The visitor steered the deck: show that slide and stop the clock.
   *
   * Both halves matter. Advancing without pausing would mean the deck carrying
   * on from wherever it was pushed to, so a visitor who went back to slide one
   * to re-read it would have it taken away again seven seconds later — having
   * just demonstrated, by pressing a button, that they are reading at their
   * own pace.
   */
  const goTo = useCallback(
    (to: number) => {
      setPaused(true);
      /* Read off the *unnormalised* target, which is what makes the wrap
         behave: `next` on the last slide asks for index 3 of 3 and is still
         going forwards, where a direction derived from the wrapped value (0)
         would animate it backwards past every slide it skipped. */
      setDirection(to < index ? -1 : 1);
      setIndex(((to % total) + total) % total);
    },
    [index, total],
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const previous = useCallback(() => goTo(index - 1), [goTo, index]);

  /** The pause button. The only control that changes `paused` without also
      changing the slide, and the only way back to rotating once stopped. */
  const togglePaused = useCallback(() => setPaused((was) => !was), []);

  const suspendProps = useMemo(
    () => ({
      onPointerEnter: () => setSuspended(true),
      onPointerLeave: () => setSuspended(false),
      onFocus: () => setSuspended(true),
      onBlur: () => setSuspended(false),
    }),
    [],
  );

  return {
    index,
    direction,
    /* Guarded for the same reason `goTo` wraps: `slides` is a prop, and a deck
       that shrank under a stale index would throw rather than show a slide. */
    slide: slides[Math.min(index, total - 1)] ?? slides[0]!,
    total,
    rotating,
    paused,
    togglePaused,
    goTo,
    next,
    previous,
    live: rotating ? 'off' : 'polite',
    suspendProps,
  };
}
